<!--
@component
VenueMap

Small basemap (single-country or merged host-region) with a pulsing dot at
the supplied lat/lon. Topojson is fetched at runtime from the jsdelivr CDN
(see `ATLAS_CLIENT_VERSION` in `./geometry.ts`); fetch failures degrade to
an empty SVG.
-->

<script lang="ts">
  import type { GeoProjection } from 'd3-geo';
  import { getTopoEntry, loadGeometry, loadCombinedGeometry } from './geometry';

  interface Props {
    lat?: number;
    lon?: number;
    /** Host country name, used to look up which topojson to render. */
    country?: string;
    /** Optional accessible label. If omitted, the map is treated as decorative. */
    label?: string;
    /**
     * When `true`, merge every topology listed in `topoLookup.json` into a
     * single basemap so all host countries are shown together. Combined mode
     * always uses `geoMercator`; per-country projections are ignored.
     */
    combined?: boolean;
  }

  const WIDTH = 65;
  const HEIGHT = 65;
  // Combined basemap is wider (covers MX + US + CA) so use a wider canvas.
  const COMBINED_WIDTH = 100;
  const COMBINED_HEIGHT = 100;
  const PULSE_PADDING = 5;

  let {
    lat = 51.1657,
    lon = 10.4515,
    country = 'Mexico',
    label,
    combined = false,
  }: Props = $props();

  // Unique id so multiple instances don't collide on the SVG filter id.
  const uid = $props.id();
  const filterId = `venue-blur-${uid}`;

  let pathD = $state('');
  let cx = $state(0);
  let cy = $state(0);

  const svgWidth = $derived(combined ? COMBINED_WIDTH : WIDTH);
  const svgHeight = $derived(combined ? COMBINED_HEIGHT : HEIGHT);

  // Log the fallback warning at most once per component instance.
  let warnedFallback = false;
  const warnOnce = (reason: string) => {
    if (warnedFallback) return;
    warnedFallback = true;
    console.warn(
      `[VenueMap] Rendering empty basemap as a graceful fallback (${reason}).`
    );
  };

  $effect(() => {
    let cancelled = false;

    const apply = (
      geometry: { pathD: string; projection: GeoProjection } | null
    ) => {
      if (cancelled) return;
      if (!geometry) {
        pathD = '';
        cx = 0;
        cy = 0;
        warnOnce(
          combined ? 'combined geometry unavailable' : `country "${country}"`
        );
        return;
      }
      pathD = geometry.pathD;
      const projected = geometry.projection([lon, lat]) ?? [0, 0];
      cx = projected[0];
      cy = projected[1];
    };

    const handleError = (err: unknown) => {
      if (cancelled) return;
      console.error('[VenueMap] Unexpected error loading geometry:', err);
      apply(null);
    };

    if (combined) {
      loadCombinedGeometry(COMBINED_WIDTH, COMBINED_HEIGHT)
        .then(apply)
        .catch(handleError);
    } else {
      const entry = getTopoEntry(country);
      if (!entry) {
        pathD = '';
        cx = 0;
        cy = 0;
        warnOnce(`unknown country "${country}"`);
        return;
      }
      loadGeometry(entry, WIDTH, HEIGHT).then(apply).catch(handleError);
    }

    return () => {
      cancelled = true;
    };
  });
</script>

<div class="venue-map my-2">
  {#if pathD}
    <svg
      width={svgWidth}
      height={svgHeight + PULSE_PADDING}
      role={label ? 'img' : 'presentation'}
      aria-hidden={label ? undefined : 'true'}
      aria-label={label}
    >
      <defs>
        <filter id={filterId} x="0" y="0">
          <feGaussianBlur in="SourceGraphic" stdDeviation="2" />
        </filter>
      </defs>

      <g>
        <path class="shadow" d={pathD} style:filter="url(#{filterId})" />
        <path d={pathD} />
        <circle class="outer" {cx} {cy} r="5" />
        <circle class="inner" {cx} {cy} r="2" />
      </g>
    </svg>
  {:else}
    <div
      class="map-loading"
      style:width="{svgWidth}px"
      style:height="{svgHeight + PULSE_PADDING}px"
    ></div>
  {/if}
</div>

<style lang="scss">
  @use '../../../styles/mixins' as mixins;
  .map-loading {
    @include mixins.loading;
    border-radius: 50%;
  }
  .venue-map {
    display: flex;
    justify-content: center;
  }
  svg {
    overflow: visible;
    path {
      fill: var(--theme-colour-muted-grey);
      stroke: var(--theme-style-border-subtle-colour);
      stroke-linejoin: round;
      stroke-linecap: round;
      &.shadow {
        fill: var(--theme-colour-dark-grey);
        transform: translate(2px, 2px);
      }
    }
    circle {
      shape-rendering: crispEdges;
      &.inner {
        fill: var(--theme-colour-live-red);
      }
      &.outer {
        stroke: var(--theme-colour-live-red);
        fill: none;
        animation: pulse 2s infinite ease-in-out;
        transform-origin: center;
        transform-box: fill-box;
      }
    }
  }

  @keyframes pulse {
    0% {
      transform: scale(0.2);
      opacity: 1;
    }
    100% {
      transform: scale(1.2);
      opacity: 0;
    }
  }
</style>
